// react
import { useCallback, useMemo } from 'react';

// tanstack
// hooks/useSeriesFilterParam.js
import { useNavigate, useSearch } from '@tanstack/react-router';

const enc = (o) => {
  if (!o) return null;
  return encodeURIComponent(JSON.stringify(o));
};
const dec = (s) => {
  if (!s) return null;
  try {
    const decoded = decodeURIComponent(s);
    return JSON.parse(decoded);
  } catch (e) {
    console.error('Failed to decode:', s, e);
    return null;
  }
};

export function useSeriesFiltersParam(seriesKey) {
  const search = useSearch({ strict: false });
  const navigate = useNavigate();

  // Get the array of series from search params
  // TanStack Router stores arrays directly in search object
  const seriesArray = useMemo(
    () => search?.[seriesKey] || [],
    [search, seriesKey],
  );

  // Ensure it's always an array
  // If it's a string (from URL), it might be comma-separated encoded values
  const normalizedArray = useMemo(() => {
    if (typeof seriesArray === 'string') {
      // TanStack Router sometimes joins arrays with commas in the URL
      // Split by comma, but only if it looks like multiple encoded values
      return seriesArray.includes('%')
        ? seriesArray.split(',').filter(Boolean)
        : [seriesArray];
    } else if (Array.isArray(seriesArray)) {
      return seriesArray;
    } else {
      return seriesArray ? [seriesArray] : [];
    }
  }, [seriesArray]);

  // Decode only when this key's values actually change
  const series = useMemo(() => {
    return normalizedArray
      .map((s, index) => {
        try {
          const decoded = dec(s);
          return decoded ? { ...decoded, __index: index } : null;
        } catch (e) {
          console.error('Failed to decode series item:', s, e);
          return null;
        }
      })
      .filter(Boolean);
  }, [normalizedArray]);

  const updateSearch = useCallback(
    (updater) => {
      navigate({
        search: (prev) => updater(prev),
        replace: true,
      });
    },
    [navigate],
  );

  const clearSeries = useCallback(() => {
    updateSearch((prev) => {
      const next = { ...prev };
      delete next[seriesKey];
      return next;
    });
  }, [seriesKey, updateSearch]);

  const addSeries = useCallback(
    (filtersObj) => {
      updateSearch((prev) => {
        const current = prev[seriesKey] || [];
        const currentArray = Array.isArray(current) ? current : [current];
        return {
          ...prev,
          [seriesKey]: [...currentArray, enc(filtersObj)],
        };
      });
    },
    [seriesKey, updateSearch],
  );

  const insertSeriesAt = useCallback(
    (index, filtersObj) => {
      updateSearch((prev) => {
        const current = prev[seriesKey] || [];
        const currentArray = Array.isArray(current) ? current : [current];
        const safeIndex = Math.max(0, Math.min(index, currentArray.length));

        return {
          ...prev,
          [seriesKey]: [
            ...currentArray.slice(0, safeIndex),
            enc(filtersObj),
            ...currentArray.slice(safeIndex),
          ],
        };
      });
    },
    [seriesKey, updateSearch],
  );

  const updateSeriesAt = useCallback(
    (index, updater) => {
      updateSearch((prev) => {
        const current = prev[seriesKey] || [];
        const currentArray = Array.isArray(current) ? current : [current];

        if (index < 0 || index >= currentArray.length) return prev;

        const currentObj = dec(currentArray[index]);
        const nextObj =
          typeof updater === 'function' ? updater(currentObj) : updater;

        return {
          ...prev,
          [seriesKey]: currentArray.map((val, i) =>
            i === index ? enc(nextObj) : val,
          ),
        };
      });
    },
    [seriesKey, updateSearch],
  );

  const removeSeriesAt = useCallback(
    (index) => {
      updateSearch((prev) => {
        const current = prev[seriesKey] || [];
        const currentArray = Array.isArray(current) ? current : [current];

        if (index < 0 || index >= currentArray.length) return prev;

        return {
          ...prev,
          [seriesKey]: currentArray.filter((_, i) => i !== index),
        };
      });
    },
    [seriesKey, updateSearch],
  );

  const ensureDefaultSeries = useCallback(
    (defaultsArray) => {
      if (!defaultsArray?.length) return;

      // Check current state synchronously
      const current = search?.[seriesKey];
      const currentArray = current
        ? Array.isArray(current)
          ? current
          : [current]
        : [];

      // Only set defaults if there are no existing series
      if (currentArray.length === 0) {
        navigate({
          search: (prev) => ({
            ...prev,
            [seriesKey]: defaultsArray.map(enc),
          }),
          replace: true,
        });
      }
    },
    [seriesKey, search, navigate],
  );

  return {
    series,
    addSeries,
    insertSeriesAt,
    updateSeriesAt,
    removeSeriesAt,
    clearSeries,
    ensureDefaultSeries,
  };
}
